import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Trash2, Package, ShoppingCart, Users, DollarSign, X, Upload, Loader2 } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

const EMPTY = { name: "", scientific_name: "", category: "insects", description: "", image_url: "", featured: false, active: true, care_tips: [], variants: [{ size: "", price: "", in_stock: true }] };
const FULFILL = ["processing", "shipped", "delivered"];

export default function Admin() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [sales, setSales] = useState([]);
  const [orderQuery, setOrderQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    if (ready && (!user || user.role !== "admin")) nav("/auth", { state: { from: "/admin" } });
  }, [ready, user, nav]);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/products?include_inactive=true").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/sales?days=14").then((r) => setSales(r.data.series)).catch(() => {});
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  const saveProduct = async (form) => {
    const payload = { ...form, variants: form.variants.map((v) => ({ size: v.size, price: parseFloat(v.price) || 0, in_stock: v.in_stock !== false })) };
    try {
      if (form.id) await api.put(`/admin/products/${form.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Product saved");
      setEditing(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load();
  };
  const saveOrder = async (id, patch) => {
    await api.put(`/admin/orders/${id}`, patch);
    setOrders((p) => p.map((o) => (o.id === id ? { ...o, ...patch } : o)));
    toast.success(patch.fulfillment === "shipped" ? "Marked shipped — customer notified" : "Order updated");
  };

  if (!stats) return <div className="App bg-[#080808] pt-40 text-center text-[#8A8782]">Loading dashboard…</div>;

  const cards = [
    { icon: DollarSign, label: "Revenue", value: `$${stats.revenue.toFixed(2)}` },
    { icon: ShoppingCart, label: "Paid Orders", value: stats.orders },
    { icon: Package, label: "Products", value: stats.products },
    { icon: Users, label: "Customers", value: stats.customers },
  ];

  return (
    <div className="App bg-[#080808] pt-16 min-h-screen">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-12">
        <p className="overline mb-3">Control Room</p>
        <h1 className="font-display font-light text-5xl md:text-6xl tracking-tighter mb-10">Admin.</h1>

        <div className="flex gap-6 border-b border-[#222] pb-4 mb-10">
          {["overview", "products", "orders"].map((t) => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
              className={`overline transition-colors ${tab === t ? "text-[#C7F000]" : "text-[#8A8782] hover:text-[#F4F1EC]"}`}>{t}</button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="space-y-px">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#222] border border-[#222]">
              {cards.map((c, i) => (
                <motion.div key={c.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }} className="bg-[#080808] p-8">
                  <c.icon size={20} className="text-[#C7F000] mb-6" />
                  <p className="text-3xl font-display">{c.value}</p>
                  <p className="overline mt-1">{c.label}</p>
                </motion.div>
              ))}
            </div>

            <div className="border border-[#222] border-t-0 p-8" data-testid="sales-chart">
              <div className="flex items-baseline justify-between mb-8">
                <p className="overline">Revenue · Last 14 Days</p>
                <p className="text-xs text-[#8A8782]">Total ${sales.reduce((s, d) => s + d.revenue, 0).toFixed(2)}</p>
              </div>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <AreaChart data={sales} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#C7F000" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#C7F000" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#1a1a1a" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="#8A8782" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#222" }} />
                    <YAxis stroke="#8A8782" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
                    <Tooltip contentStyle={{ background: "#121212", border: "1px solid #222", fontFamily: "JetBrains Mono", fontSize: 12 }} labelStyle={{ color: "#8A8782" }} itemStyle={{ color: "#C7F000" }} formatter={(v) => [`$${v.toFixed(2)}`, "Revenue"]} />
                    <Area type="monotone" dataKey="revenue" stroke="#C7F000" strokeWidth={2} fill="url(#rev)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {tab === "products" && (
          <div>
            <button data-testid="add-product-btn" onClick={() => setEditing(EMPTY)} className="btn-toxic px-6 py-3 inline-flex items-center gap-2 mb-8"><Plus size={16} /> New Product</button>
            <div className="border border-[#222]" data-testid="admin-products">
              {products.map((p) => (
                <div key={p.id} className="border-b border-[#1a1a1a] p-5 flex items-center gap-5">
                  <img src={imgSrc(p.image_url)} alt={p.name} className="w-14 h-14 object-cover bg-[#121212]" />
                  <div className="flex-1">
                    <p className="font-display text-xl">{p.name} {!p.active && <span className="overline text-[#D94026]">· hidden</span>}</p>
                    <p className="text-[11px] text-[#8A8782]">{p.category} · {p.variants.length} sizes</p>
                  </div>
                  <button onClick={() => setEditing({ ...p, care_tips: p.care_tips || [], variants: p.variants.map((v) => ({ size: v.size, price: v.price, in_stock: v.in_stock !== false })) })} className="overline hover:text-[#C7F000]">Edit</button>
                  <button data-testid={`del-product-${p.slug}`} onClick={() => delProduct(p.id)} className="text-[#8A8782] hover:text-[#D94026]"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div>
            <div className="flex flex-col md:flex-row gap-3 mb-6">
              <input data-testid="order-search" placeholder="Search by customer email…" value={orderQuery}
                onChange={(e) => setOrderQuery(e.target.value)} className="field flex-1" />
              <select data-testid="order-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="field md:w-56 bg-[#080808] text-xs uppercase tracking-wider cursor-pointer">
                <option value="" className="bg-[#121212]">All statuses</option>
                {FULFILL.map((f) => <option key={f} value={f} className="bg-[#121212]">{f}</option>)}
              </select>
            </div>
            <div className="border border-[#222]" data-testid="admin-orders">
              {(() => {
                const filtered = orders.filter((o) =>
                  (!statusFilter || o.fulfillment === statusFilter) &&
                  (!orderQuery || (o.customer_email || "").toLowerCase().includes(orderQuery.toLowerCase()) || o.id.toLowerCase().includes(orderQuery.toLowerCase())));
                if (orders.length === 0) return <p className="p-8 text-[#8A8782] text-sm">No paid orders yet.</p>;
                if (filtered.length === 0) return <p className="p-8 text-[#8A8782] text-sm" data-testid="orders-empty">No orders match your search.</p>;
                return filtered.map((o) => <OrderRow key={o.id} o={o} onSave={saveOrder} onView={() => setDetail(o)} />);
              })()}
            </div>
          </div>
        )}
      </div>

      {editing && <ProductModal init={editing} onClose={() => setEditing(null)} onSave={saveProduct} />}
      {detail && <OrderDetailModal o={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function ProductModal({ init, onClose, onSave }) {
  const [f, setF] = useState(init);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const upd = (k, v) => setF({ ...f, [k]: v });
  const updVar = (i, k, v) => setF({ ...f, variants: f.variants.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)) });

  const doUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, image_url: data.url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#0c0c0c] border border-[#222] w-full max-w-lg p-8 my-8" data-testid="product-modal">
        <div className="flex justify-between items-center mb-8">
          <h2 className="font-display text-3xl">{f.id ? "Edit" : "New"} Product</h2>
          <button onClick={onClose} className="text-[#8A8782] hover:text-[#F4F1EC]"><X size={20} /></button>
        </div>
        <div className="space-y-5">
          <div><label className="overline">Name</label><input data-testid="pm-name" value={f.name} onChange={(e) => upd("name", e.target.value)} className="field" /></div>
          <div><label className="overline">Scientific name</label><input value={f.scientific_name} onChange={(e) => upd("scientific_name", e.target.value)} className="field" /></div>
          <div><label className="overline">Category</label>
            <select value={f.category} onChange={(e) => upd("category", e.target.value)} className="field bg-[#0c0c0c]">
              <option value="insects" className="bg-[#121212]">insects</option>
              <option value="rodents" className="bg-[#121212]">rodents</option>
            </select>
          </div>
          <div>
            <label className="overline">Product photo</label>
            <div className="flex items-center gap-4 mt-2">
              <div className="w-20 h-20 bg-[#121212] border border-[#222] overflow-hidden flex-shrink-0">
                {f.image_url ? <img src={imgSrc(f.image_url)} alt="preview" className="w-full h-full object-cover" data-testid="pm-image-preview" /> : null}
              </div>
              <input ref={fileRef} type="file" accept="image/*" onChange={doUpload} className="hidden" data-testid="pm-file-input" />
              <button type="button" data-testid="pm-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="px-4 py-3 border border-[#222] overline inline-flex items-center gap-2 hover:border-[#C7F000] transition-colors disabled:opacity-50">
                {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Upload size={14} /> Upload Photo</>}
              </button>
            </div>
            <input placeholder="…or paste an image URL" value={f.image_url} onChange={(e) => upd("image_url", e.target.value)} className="field mt-3 text-xs" />
          </div>
          <div><label className="overline">Description</label><textarea value={f.description} onChange={(e) => upd("description", e.target.value)} className="field resize-none" rows={2} /></div>
          <div><label className="overline">Care & gut-loading tips (one per line)</label>
            <textarea data-testid="pm-care-tips" value={(f.care_tips || []).join("\n")} onChange={(e) => upd("care_tips", e.target.value.split("\n").map((t) => t.trim()).filter(Boolean))} className="field resize-none" rows={4} placeholder="Keep at 75-85°F…&#10;Gut-load 24h before feeding…" /></div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-xs text-[#8A8782]"><input type="checkbox" checked={f.featured} onChange={(e) => upd("featured", e.target.checked)} /> Featured</label>
            <label className="flex items-center gap-2 text-xs text-[#8A8782]"><input type="checkbox" checked={f.active} onChange={(e) => upd("active", e.target.checked)} /> Active</label>
          </div>
          <div>
            <div className="flex justify-between items-center mb-3"><label className="overline">Variants (size · price · stock)</label>
              <button onClick={() => setF({ ...f, variants: [...f.variants, { size: "", price: "", in_stock: true }] })} className="overline hover:text-[#C7F000]">+ add</button></div>
            {f.variants.map((v, i) => (
              <div key={i} className="flex gap-3 mb-2 items-center">
                <input data-testid={`pm-size-${i}`} placeholder="Size" value={v.size} onChange={(e) => updVar(i, "size", e.target.value)} className="field flex-1" />
                <input data-testid={`pm-price-${i}`} placeholder="0.00" type="number" step="0.01" value={v.price} onChange={(e) => updVar(i, "price", e.target.value)} className="field w-24" />
                <button type="button" data-testid={`pm-stock-${i}`} onClick={() => updVar(i, "in_stock", v.in_stock === false)}
                  className={`px-3 py-2 text-[10px] uppercase tracking-wider border whitespace-nowrap transition-colors ${v.in_stock === false ? "border-[#D94026] text-[#D94026]" : "border-[#C7F000] text-[#C7F000]"}`}>
                  {v.in_stock === false ? "Sold out" : "In stock"}
                </button>
                {f.variants.length > 1 && <button onClick={() => setF({ ...f, variants: f.variants.filter((_, idx) => idx !== i) })} className="text-[#8A8782] hover:text-[#D94026]"><Trash2 size={14} /></button>}
              </div>
            ))}
          </div>
          <button data-testid="pm-save" onClick={() => onSave(f)} className="btn-toxic w-full py-4 mt-4">Save Product</button>
        </div>
      </motion.div>
    </div>
  );
}

function OrderRow({ o, onSave, onView }) {
  const [status, setStatus] = useState(o.fulfillment);
  const [tn, setTn] = useState(o.tracking_number || "");
  const [carrier, setCarrier] = useState(o.carrier || "");
  const showTracking = status === "shipped";

  const onSelect = (val) => {
    setStatus(val);
    if (val !== "shipped") onSave(o.id, { fulfillment: val });
  };

  return (
    <div className="border-b border-[#1a1a1a] p-5 text-sm">
      <div className="grid md:grid-cols-6 gap-4 items-center">
        <div className="min-w-0">
          <button data-testid={`view-order-${o.id}`} onClick={onView} className="text-xs text-left hover:text-[#C7F000] transition-colors block">#{o.id.slice(-8)}</button>
          {o.customer_email && <span className="text-[10px] text-[#8A8782] truncate block" title={o.customer_email}>{o.customer_email}</span>}
        </div>
        <span className="text-xs text-[#8A8782]">{new Date(o.created_at).toLocaleDateString()}</span>
        <span className="text-xs">{o.items.reduce((s, i) => s + i.quantity, 0)} units</span>
        <span>${o.amount.toFixed(2)}</span>
        <select data-testid={`fulfill-${o.id}`} value={status} onChange={(e) => onSelect(e.target.value)}
          className="field text-xs uppercase tracking-wider bg-[#080808] cursor-pointer">
          {FULFILL.map((f) => <option key={f} value={f} className="bg-[#121212]">{f}</option>)}
        </select>
        <button data-testid={`view-btn-${o.id}`} onClick={onView} className="overline text-[#8A8782] hover:text-[#C7F000] transition-colors md:text-right">View →</button>
      </div>
      {showTracking && (
        <div className="mt-4 pt-4 border-t border-[#1a1a1a] flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1"><label className="overline">Carrier</label>
            <input data-testid={`carrier-${o.id}`} placeholder="UPS, FedEx…" value={carrier} onChange={(e) => setCarrier(e.target.value)} className="field" /></div>
          <div className="flex-1"><label className="overline">Tracking number</label>
            <input data-testid={`tracking-${o.id}`} placeholder="1Z…" value={tn} onChange={(e) => setTn(e.target.value)} className="field" /></div>
          <button data-testid={`ship-save-${o.id}`} onClick={() => onSave(o.id, { fulfillment: "shipped", tracking_number: tn, carrier })}
            className="btn-toxic px-6 py-3 whitespace-nowrap">Mark Shipped & Email</button>
        </div>
      )}
    </div>
  );
}

function OrderDetailModal({ o, onClose }) {
  const addr = o.shipping_address;
  const row = (label, val) => (
    <div className="flex justify-between py-1 text-sm"><span className="text-[#8A8782]">{label}</span><span>{val}</span></div>
  );
  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#0c0c0c] border border-[#222] w-full max-w-lg p-8 my-8" data-testid="order-detail-modal">
        <div className="flex justify-between items-center mb-6">
          <div>
            <p className="overline">Order</p>
            <h2 className="font-display text-3xl">#{o.id.slice(-8)}</h2>
          </div>
          <button onClick={onClose} className="text-[#8A8782] hover:text-[#F4F1EC]"><X size={20} /></button>
        </div>

        <p className="overline mb-3">Items</p>
        <div className="border-t border-[#222] mb-6">
          {o.items.map((i, idx) => (
            <div key={idx} className="flex justify-between py-2 border-b border-[#1a1a1a] text-sm">
              <span>{i.quantity} × {i.name || i.lookup_key}{i.size ? ` · ${i.size}` : ""}</span>
              <span className="text-[#8A8782]">${(i.unit_amount * i.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>

        <div className="mb-6">
          {row("Subtotal", `$${(o.subtotal ?? o.amount).toFixed(2)}`)}
          {o.discount_amount ? row(`Bulk discount (${o.discount_percent}%)`, `-$${o.discount_amount.toFixed(2)}`) : null}
          {row("Shipping" + (o.shipping_method ? ` (${o.shipping_method})` : ""), o.shipping ? `$${o.shipping.toFixed(2)}` : "FREE")}
          <div className="flex justify-between py-2 border-t border-[#222] mt-1 text-[#F4F1EC]"><span>Total</span><span data-testid="order-detail-total">${o.amount.toFixed(2)}</span></div>
        </div>

        <p className="overline mb-2">Customer</p>
        <p className="text-sm mb-6" data-testid="order-detail-email">{o.customer_email || "Guest"}</p>

        <p className="overline mb-2">Shipping address</p>
        {addr ? (
          <div className="text-sm text-[#8A8782] leading-relaxed" data-testid="order-detail-address">
            {addr.name && <div className="text-[#F4F1EC]">{addr.name}</div>}
            <div>{addr.line1}</div>
            {addr.line2 && <div>{addr.line2}</div>}
            <div>{[addr.city, addr.state, addr.postal_code].filter(Boolean).join(", ")}</div>
            <div>{addr.country}</div>
          </div>
        ) : <p className="text-sm text-[#8A8782]">No address on file.</p>}

        {(o.tracking_number || o.carrier) && (
          <>
            <p className="overline mt-6 mb-2">Tracking</p>
            <p className="text-sm text-[#C7F000]">{[o.carrier, o.tracking_number].filter(Boolean).join(" · ")}</p>
          </>
        )}
      </motion.div>
    </div>
  );
}
